
import React, { useState } from 'react';
import { PDFViewer } from '@/components/PDFViewer';
import { InsuranceDetailsPanel } from '@/components/InsuranceDetailsPanel';
import { DocuSealEmbedded } from '@/components/DocuSealEmbedded';
import { InsuranceAnalysisResult } from '@/types/insurance';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { getDocuSealSigningLink } from '@/lib/fileHandler';
import { FileSignature, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import '../styles/rich-message.css';

interface PublicOfferViewProps {
  title: string;
  fileUrl?: string | null;
  analysisResult?: InsuranceAnalysisResult | null;
  offerType: 'offer' | 'comparison';
  includePdfViewer?: boolean;
  includeSignatureSection?: boolean;
  fileId?: string;
  companyName?: string;
  companyLogo?: string | null;
  offerContent?: string | null;
}

interface InsuredPerson {
  name: string;
  coverage?: string;
  premium?: string;
}

const PublicOfferView: React.FC<PublicOfferViewProps> = ({
  title,
  fileUrl,
  analysisResult,
  offerType,
  includePdfViewer = true,
  includeSignatureSection = true,
  fileId,
  companyName,
  companyLogo,
  offerContent
}) => {
  const [signingUrl, setSigningUrl] = useState<string | null>(null);
  const [isSigningOpen, setIsSigningOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Parse any insured persons from the analysis results
  const insuredPersons: InsuredPerson[] = [];
  
  // Extract insured persons from analysis if available
  if (analysisResult && analysisResult.coverage) {
    const coverageText = analysisResult.coverage;
    // Try to identify persons mentioned in the coverage
    const personMatches = coverageText.match(/(?:Person|Versicherte Person|Versicherungsnehmer|Familie):\s*([^\n,]+)/gi);
    if (personMatches && personMatches.length > 0) {
      personMatches.forEach(match => {
        const name = match.split(':')[1]?.trim();
        if (name) {
          insuredPersons.push({ 
            name,
            coverage: "Details im Dokument",
            premium: analysisResult.premium || "Siehe Dokument"
          });
        }
      });
    }
  }
  
  // If no persons were detected, add a generic one
  if (insuredPersons.length === 0 && analysisResult) {
    insuredPersons.push({
      name: "Versicherungsnehmer",
      coverage: analysisResult.coverage || "Siehe Details",
      premium: analysisResult.premium || "Siehe Details"
    });
  }

  const handleSignButtonClick = async () => {
    if (!fileId) {
      toast({
        title: "Fehler",
        description: "Keine Datei-ID gefunden",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const email = prompt("Bitte geben Sie Ihre E-Mail-Adresse ein:");
      if (!email) {
        setIsLoading(false);
        return;
      }

      // Get DocuSeal signing link through edge function
      const response = await fetch('/api/get-signing-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileId,
          recipientEmail: email
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get signing link');
      }

      const data = await response.json();
      setSigningUrl(data.signingUrl);
      setIsSigningOpen(true);
    } catch (error) {
      console.error('Error getting signing link:', error);
      toast({
        title: "Fehler",
        description: "Konnte keinen Signaturlink erstellen",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Parse duration from analysis result
  let durationInfo = "Informationen zur Vertragslaufzeit nicht verfügbar";
  if (analysisResult?.duration) {
    durationInfo = analysisResult.duration;
  }

  // Extract and format the summary
  const summary = analysisResult?.summary || "Keine Zusammenfassung verfügbar";

  // Determine insurance type
  let insuranceType = "Versicherung";
  if (title) {
    if (title.toLowerCase().includes("kranken") || title.toLowerCase().includes("gesundheit")) 
      insuranceType = "Krankenversicherung";
    else if (title.toLowerCase().includes("haftpflicht")) 
      insuranceType = "Haftpflichtversicherung";
    else if (title.toLowerCase().includes("hausrat")) 
      insuranceType = "Hausratversicherung";
    else if (title.toLowerCase().includes("auto") || title.toLowerCase().includes("kfz")) 
      insuranceType = "KFZ-Versicherung";
    else if (title.toLowerCase().includes("recht")) 
      insuranceType = "Rechtsschutzversicherung";
  }

  // If we have HTML content from Claude, use it directly
  if (offerContent) {
    return (
      <div className="min-h-screen h-screen flex flex-col bg-white">
        {/* Company header */}
        {companyName && (
          <header className="w-full bg-gray-50 border-b border-gray-200 py-4 sticky top-0 z-10 flex-shrink-0">
            <div className="container mx-auto px-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {companyLogo && (
                  <img 
                    src={companyLogo} 
                    alt={`${companyName} Logo`} 
                    className="h-10 w-auto object-contain"
                  />
                )}
                <h1 className="text-xl font-medium text-gray-800">{companyName}</h1>
              </div>
              <div className="flex gap-2">
                {fileUrl && (
                  <Button 
                    variant="outline" 
                    onClick={() => window.open(fileUrl, '_blank')}
                    className="flex items-center gap-1"
                  >
                    <Download className="h-4 w-4" />
                    <span>Dokument</span>
                  </Button>
                )}
                {includeSignatureSection && (
                  <Button 
                    onClick={handleSignButtonClick}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    disabled={isLoading}
                  >
                    <FileSignature className="mr-2 h-4 w-4" />
                    {isLoading ? "Wird geladen..." : "Unterschreiben"}
                  </Button>
                )}
              </div>
            </div>
          </header>
        )}

        {/* Generated content from Claude with scrolling */}
        <ScrollArea className="flex-grow">
          <div 
            className="pt-6 pb-12 container mx-auto px-4 rich-content"
            dangerouslySetInnerHTML={{ __html: offerContent }}
          />
        </ScrollArea>

        {/* DocuSeal Signing Dialog */}
        <Dialog open={isSigningOpen} onOpenChange={setIsSigningOpen}>
          <DialogContent className="max-w-4xl h-[80vh]">
            {signingUrl && (
              <DocuSealEmbedded 
                signingUrl={signingUrl} 
                onComplete={() => setIsSigningOpen(false)}
                height="100%" 
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Default view if no custom HTML content is available
  return (
    <div className="min-h-screen h-screen flex flex-col bg-white">
      {/* Company header */}
      {companyName && (
        <header className="w-full bg-gray-50 border-b border-gray-200 py-4 sticky top-0 z-10 flex-shrink-0">
          <div className="container mx-auto px-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {companyLogo && (
                <img 
                  src={companyLogo} 
                  alt={`${companyName} Logo`} 
                  className="h-10 w-auto object-contain"
                />
              )}
              <h1 className="text-xl font-medium text-gray-800">{companyName}</h1>
            </div>
          </div>
        </header>
      )}

      <ScrollArea className="flex-grow">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left column - Details and analysis */}
            <div className="order-2 lg:order-1">
              <div className="space-y-6">
                {/* Insurance title and type */}
                <div className="bg-gradient-to-r from-indigo-600 to-blue-500 rounded-lg p-6 text-white shadow-lg">
                  <h1 className="text-2xl font-bold mb-2">{title}</h1>
                  <p className="text-lg opacity-90">{insuranceType}</p>
                </div>
                
                {/* Summary section */}
                <Card>
                  <CardHeader>
                    <CardTitle>Zusammenfassung</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{summary}</p>
                  </CardContent>
                </Card>

                {/* Tabs for different sections */}
                <Tabs defaultValue="coverage" className="w-full">
                  <TabsList className="w-full grid grid-cols-4">
                    <TabsTrigger value="coverage">Leistungen</TabsTrigger>
                    <TabsTrigger value="details">Vertrag</TabsTrigger>
                    <TabsTrigger value="persons">Versicherte</TabsTrigger>
                    <TabsTrigger value="documents">Dokumente</TabsTrigger>
                  </TabsList>
                  
                  {/* Coverage tab */}
                  <TabsContent value="coverage" className="pt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Leistungsumfang</CardTitle>
                        <CardDescription>Was ist in diesem Angebot enthalten</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {analysisResult?.coverage ? (
                          <div className="text-gray-700 whitespace-pre-line">
                            {analysisResult.coverage}
                          </div>
                        ) : (
                          <p className="text-gray-500 italic">Keine Leistungsinformationen verfügbar</p>
                        )}
                        
                        {/* Benefits list */}
                        {analysisResult?.benefits && analysisResult.benefits.length > 0 && (
                          <div className="mt-4">
                            <h4 className="font-medium mb-2">Vorteile:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              {analysisResult.benefits.map((benefit, index) => (
                                <li key={index} className="text-gray-700">{benefit}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Exclusions list */}
                        {analysisResult?.exclusions && analysisResult.exclusions.length > 0 && (
                          <div className="mt-4">
                            <h4 className="font-medium mb-2">Ausschlüsse:</h4>
                            <ul className="list-disc pl-5 space-y-1">
                              {analysisResult.exclusions.map((exclusion, index) => (
                                <li key={index} className="text-gray-700">{exclusion}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  {/* Details tab */}
                  <TabsContent value="details" className="pt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Vertragsdetails</CardTitle>
                        <CardDescription>Laufzeit, Kündigungsfristen und Kosten</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Duration information */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-medium text-sm text-gray-500 mb-1">Laufzeit</h4>
                            <p className="text-gray-700">{durationInfo}</p>
                          </div>
                          
                          {/* Premium information */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-medium text-sm text-gray-500 mb-1">Kosten</h4>
                            <p className="text-gray-700">{analysisResult?.premium || "Keine Preisinformation verfügbar"}</p>
                          </div>
                        </div>
                        
                        {/* Termination information - expanded */}
                        <div className="mt-4">
                          <h4 className="font-medium mb-2">Kündigungsinformationen:</h4>
                          <div className="text-gray-700">
                            {durationInfo.includes("Kündigung") ? durationInfo : (
                              <p>
                                Die reguläre Kündigungsfrist beträgt in der Regel einen oder drei Monate zum Ablauf 
                                der Vertragslaufzeit. Für die genauen Kündigungsfristen und -bedingungen schauen Sie
                                bitte in die Vertragsbedingungen oder fragen Sie Ihren Berater.
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  {/* Insured persons tab */}
                  <TabsContent value="persons" className="pt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Versicherte Personen</CardTitle>
                        <CardDescription>Wer ist in diesem Vertrag versichert</CardDescription>
                      </CardHeader>
                      <CardContent>
                        {insuredPersons.length > 0 ? (
                          <div className="divide-y">
                            {insuredPersons.map((person, index) => (
                              <div key={index} className="py-3 flex justify-between items-center">
                                <div>
                                  <h4 className="font-medium">{person.name}</h4>
                                  <p className="text-sm text-gray-500">{person.coverage}</p>
                                </div>
                                {person.premium && (
                                  <div className="text-right">
                                    <span className="font-medium">{person.premium}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 italic">Keine Informationen zu versicherten Personen verfügbar</p>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  {/* Documents tab */}
                  <TabsContent value="documents" className="pt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Dokumente</CardTitle>
                        <CardDescription>Verfügbare Dokumente zum Vertrag</CardDescription>
                      </CardHeader>
                      <CardContent>
                        {fileUrl ? (
                          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <span className="font-medium">{title}</span>
                            <Button 
                              onClick={() => window.open(fileUrl, '_blank')}
                              className="flex items-center gap-1"
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Öffnen
                            </Button>
                          </div>
                        ) : (
                          <p className="text-gray-500 italic">Keine Dokumente verfügbar</p>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>

                {/* Call to action for signature */}
                {includeSignatureSection && (
                  <div className="mt-4 py-8 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-100 px-6">
                    <div className="flex flex-col lg:flex-row items-center justify-between">
                      <div className="mb-6 lg:mb-0 lg:mr-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">Bereit zu unterschreiben?</h3>
                        <p className="text-gray-600">
                          Sie können dieses Dokument digital mit wenigen Klicks unterschreiben.
                        </p>
                      </div>
                      <Button 
                        onClick={handleSignButtonClick}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        disabled={isLoading}
                      >
                        <FileSignature className="mr-2 h-4 w-4" />
                        {isLoading ? "Wird geladen..." : "Jetzt unterschreiben"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right column - PDF preview */}
            {includePdfViewer && fileUrl && (
              <div className="order-1 lg:order-2 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden h-[600px]">
                <PDFViewer url={fileUrl} onDownload={() => window.open(fileUrl, '_blank')} />
              </div>
            )}
          </div>
        </div>
      </ScrollArea>

      {/* DocuSeal Signing Dialog */}
      <Dialog open={isSigningOpen} onOpenChange={setIsSigningOpen}>
        <DialogContent className="max-w-4xl h-[80vh]">
          {signingUrl && (
            <DocuSealEmbedded 
              signingUrl={signingUrl} 
              onComplete={() => setIsSigningOpen(false)}
              height="100%" 
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicOfferView;
